import {AbstractControl, FormGroup} from '@angular/forms';
import {IFormControl} from '@rxweb/types';
import {IAbstractControl} from '@rxweb/types/reactive-form/i-abstract-control';
import {defer, merge, Observable, of} from 'rxjs';
import {map} from 'rxjs/operators';
import {
    FormControlExplicitStateWithValidators,
    FormControlsExplicitStates,
    FormControlsExplicitStatesWithValidators,
    TypedForm,
    TypedFormOwnControls
} from './form-types';

// Ref: http://emailregex.com/
// tslint:disable max-line-length
export const EMAIL_VALIDATION_PATTERN: RegExp =
          /^(([^<>()[\]\\.,;:\s@"]+(\.[^<>()[\]\\.,;:\s@"]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/;

export function setControlEnabledState(
    control: AbstractControl,
    isEnabled: boolean,
    options?: {
        onlySelf?: boolean;
        emitEvent?: boolean;
    }
): void {
    if (control.enabled !== isEnabled) {
        if (isEnabled) {
            control.enable(options);
        } else {
            control.disable(options);
        }
    }
}

export function updateFormControlsStateWithValidators<FORM_VALUE>(
    form: FormGroup,
    states: Partial<FormControlsExplicitStatesWithValidators<FORM_VALUE>>,
    controlUpdatingOptions?: {
        onlySelf?: boolean;
        emitEvent?: boolean;
    },
    otherOptions?: {
        markAsPristineUntouched?: boolean
    }
): void {
    const SILENT_UPDATE = {emitEvent: false};

    (Object.keys(states) as Array<keyof FORM_VALUE>).forEach(fieldName => {
        const control = form.get(fieldName as string);
        const fieldState: FormControlExplicitStateWithValidators<FORM_VALUE> = states[fieldName] as any;
        const isDisabled = fieldState && fieldState.disabled;
        const validators = fieldState && fieldState.validators || null;

        setControlEnabledState(control, !isDisabled, SILENT_UPDATE);

        if (otherOptions && otherOptions.markAsPristineUntouched) {
            control.reset(fieldState.value, SILENT_UPDATE);
        } else {
            control.setValue(fieldState.value, SILENT_UPDATE);
        }

        control.setValidators(validators);
        control.updateValueAndValidity(SILENT_UPDATE);
    });

    form.updateValueAndValidity(controlUpdatingOptions);
}

export function updateFormControlsEnabledStates<FORM_VALUE>(
    form: FormGroup,
    states: FormControlsExplicitStates<FORM_VALUE>,
    controlUpdatingOptions?: {
        onlySelf?: boolean;
        emitEvent?: boolean;
    },
    otherOptions?: {
        markAsPristineUntouched?: boolean
    }
): void {
    const SILENT_UPDATE = {emitEvent: false};

    (Object.keys(states) as Array<keyof FORM_VALUE>).forEach(fieldName => {
        const control = form.get(fieldName as string);
        const fieldState: FormControlExplicitStateWithValidators<FORM_VALUE> = states[fieldName] as any;
        const isDisabled = fieldState && fieldState.disabled;

        setControlEnabledState(control, !isDisabled, SILENT_UPDATE);
        control.updateValueAndValidity(SILENT_UPDATE);

        if (otherOptions && otherOptions.markAsPristineUntouched) {
            control.markAsPristine();
            control.markAsUntouched();
        }
    });

    form.updateValueAndValidity(controlUpdatingOptions);
}

export const getFormControlValueWithUpdatesStream = <T>(control: IAbstractControl<T> | IFormControl<T>): Observable<T> =>
    defer(() => {
        const currentValue = control.value;

        return merge(
            of(currentValue),
            control.valueChanges
        );
    });

export const getFormControlsValuesWithUpdatesStream = <T>(
    form: TypedForm<T>,
    keys: Array<keyof T>
): Observable<Partial<T>> => {
    return getFormControlValueWithUpdatesStream(form).pipe(
        map(() => {
            const controls: TypedFormOwnControls<T> = form.controls;

            return keys.reduce<Partial<{[key in keyof T]: T[key]}>>(
                (acc, key) => {
                    acc[key] = controls[key].value;

                    return acc;
                },
                {}
            );
        })
    );
};
